import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { isEmpty } from 'lodash';
import ActivityHeaderLayout from '@monorepo/shared/src/components/activities/ActivityHeaderLayout';
import MainActivityPagingControls from '@monorepo/shared/src/components/modules/Paging/components/MainActivityPagingControls';
import usePage from '@monorepo/shared/src/hooks/use-page';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { findPageAndPopulatePageData } from '@monorepo/shared/src/util/book-util';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import DoublePagePdf from '@monorepo/shared/src/components/common/Pdf/DoublePagePdf';
import {
  getActivePages,
  getPageNumber,
} from '@monorepo/shared/src/components/common/Pdf/util/pdf-util';
import PageNavigation from '@monorepo/shared/src/components/common/PageNavigation';
import WithQuery from '@monorepo/shared/src/components/common/WithQuery';
import useLetterTeaching from '@monorepo/shared/src/hooks/useLetterTeaching';
import {
  Character,
  ApplicationSection,
  BookType,
  RouteSource,
  PageLayout,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import { formatCharacter } from '@monorepo/shared/src/util/telemetry-util';
import SectionFooter from 'components/layout/SectionFooter';
import useTrialUserOmitRoutes from 'state/hooks/use-trial-user-omit-routes';
import { AppRoutes } from 'types';

const t = (key: string, options?) => translate('letterTeaching', key, options);

const TeachersGuide = (props): React.ReactElement => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_PAGE);
  const location = useLocation();
  const isMainPage = window.location.pathname === `/${AppRoutes.TEACHERS_GUIDE}`;
  const [{ books, loading, error, refetch }] = usePageStore(BookType.TEACHERS_GUIDE, props.letter);
  const teachersGuide = books?.[0];
  const [{ currentPage, totalPages, noOfPages }, { goToPage }] = usePage();
  const [character, setCharacter] = useState<Character | null>(null);
  const omitRoutes = useTrialUserOmitRoutes();
  const classes = useStyles({ isMainPage });

  const showPdf = useMemo(() => !!teachersGuide?.url, [teachersGuide]);

  useEffect(() => {
    if (!isMainPage) refetch();
  }, [isMainPage]);

  const { PageList, pagesToDisplay } = useLetterTeaching(teachersGuide, isMainPage);

  const handleOnNavToPage = (pageNumber: number) => {
    setCharacter(null);
    const pageCharacter = findPageAndPopulatePageData(books?.[0]?.pages, pageNumber)
      ?.characters?.[0];
    pageCharacter && setCharacter(pageCharacter);

    goToPage(pageNumber);
  };

  useEffect(() => {
    createTelemetryEvent();
  }, [currentPage, teachersGuide, noOfPages]);

  const createTelemetryEvent = () => {
    if (!teachersGuide) return;

    const page = pagesToDisplay?.[0]?.[0] ?? pagesToDisplay?.[0];

    if (!page) return;

    let foundCharacter = character ? { ...character } : null;
    if (!foundCharacter || isEmpty(foundCharacter)) {
      foundCharacter = {
        ...teachersGuide.characters?.find((c) => c.character === props?.letter?.toUpperCase()),
      } as any;
    }

    fireEvent({
      bookId: teachersGuide.id,
      bookType: BookType.TEACHERS_GUIDE,
      bookCode: teachersGuide.bookCode,
      pageId: page.id,
      pageNumber: `${page.pageNumber}`,
      pageLayout: noOfPages === 2 ? PageLayout.DOUBLE_PAGE : PageLayout.SINGLE_PAGE,
      section: isMainPage ? ApplicationSection.PRACTICE_BOOKS : ApplicationSection.LETTER_LEARNING,
      ...(foundCharacter &&
        !isEmpty(foundCharacter) && { character: formatCharacter(foundCharacter) }),
      route: (location?.state as any)?.from || RouteSource.DASHBOARD,
    });
  };

  const hasFooter = isMainPage && teachersGuide && teachersGuide.id;

  const BookContent = (
    <MainActivityPagingControls
      pagination={false}
      displayZoomControls
      isMainPage={isMainPage}
      hasFooter
      hasCover={isMainPage}
      bookLayout={!showPdf}
      height="80vh"
    >
      {showPdf ? (
        <DoublePagePdf
          paperWrapper={showPdf}
          leftPage={{
            documentProps: { file: teachersGuide?.url },
            pageProps: {
              pageNumber: getPageNumber('leftPage', pagesToDisplay),
            },
          }}
          rightPage={{
            documentProps: { file: teachersGuide?.url },
            pageProps: {
              pageNumber: getPageNumber('rightPage', pagesToDisplay),
            },
          }}
        />
      ) : (
        PageList
      )}
    </MainActivityPagingControls>
  );

  const PageFooter = hasFooter && (
    <SectionFooter>
      <Box className={classes.pageNavigation}>
        <PageNavigation
          activePages={getActivePages(currentPage, pagesToDisplay)}
          bookId={teachersGuide.id}
          onNavToPage={handleOnNavToPage}
          pageOffset={teachersGuide.offset}
          totalPages={totalPages}
        />
      </Box>
    </SectionFooter>
  );

  return (
    <WithQuery loading={loading} error={error}>
      {isMainPage ? (
        <ActivityHeaderLayout
          title={t('teachersGuide')}
          hasTogglePageView
          Footer={PageFooter}
          showCloseButton={false}
          omitRoutes={omitRoutes}
        >
          {BookContent}
        </ActivityHeaderLayout>
      ) : (
        BookContent
      )}
    </WithQuery>
  );
};

const useStyles = makeStyles(() => ({
  pageNavigation: {
    padding: remCalc(19),
    left: '47%',
    top: '15px',
  },
}));

export default TeachersGuide;
